export type PosConfigLevel = "franchise" | "shop" | "application";

export interface POSDisplayName {
    FR: string;
    EN: string;
    ES: string;
    IT: string;
    NL: string;
    PT: string;
}

/** Row data from the DynamicTable (project/shop/app level) */
export interface PosProjectRow {
    projectId: string;
    ProjectId?: string;
    key_project?: string;
    id_boutique?: string;
    shopId?: string;
    appId?: string;
    key_application?: string;
    Designation?: string;
    Status?: string;
    template?: POSConfig | Record<string, never>;
    isPosConfigured?: boolean;
    subRows?: PosProjectRow[];
    [key: string]: unknown;
}

/** Raw item from the generals/dataPos API (before mapping) */
export interface RawGeneralSettingItem {
    id: string;
    name?: string;
    shortName?: string;
    language?: string;
    active?: boolean | Record<string, boolean>;
    isFavorite?: boolean;
    isDefault?: boolean;
    flag?: string;
    url_image?: string;
    title?: string;
    display_name?: string;
    abbreviation?: string;
    languages?: Record<string, { name?: string; content?: string }>;
}

export interface POSSalesModeItem {
    id: string;
    name: string;
    active: boolean;
    isFavorite: boolean;
    displayName: POSDisplayName;
}

export interface POSPaymentModeItem {
    id: string;
    name: string;
    active: boolean;
    isFavorite: boolean;
    displayName: POSDisplayName;
}

export interface POSLanguageItem {
    id: string;
    name: string;
    flag: string;
    title: string;
    active: boolean;
    isFavorite: boolean;
    isDefault: boolean;
}

export interface POSInformationModeItem {
    id: string;
    name: string;
    /** active par mode de vente : { "saleModeId_or_name": boolean } */
    active: Record<string, boolean>;
    isFavorite: boolean;
    displayName: POSDisplayName;
}

export interface POSPaperlessTicketConfig {
    isActive: boolean;
    isEmailActive: boolean;
    isConfirmationEmailActive: boolean;
    isUnauthenticatedEmailActive: boolean;
}

export interface POSConfig {
    SalesMode: {
        items: POSSalesModeItem[];
    };
    PaymentMode: {
        skipStep: boolean;
        items: POSPaymentModeItem[];
    };
    Language: {
        items: POSLanguageItem[];
    };
    InformationMode: {
        skipStep: boolean;
        items: POSInformationModeItem[];
    };
    PaperlessTicket: POSPaperlessTicketConfig;
}

export const DEFAULT_SALES_MODES: POSSalesModeItem[] = [
    {
        id: "sale_mode_1",
        name: "On-site",
        active: true,
        isFavorite: true,
        displayName: {
            FR: "Sur place",
            EN: "On-site",
            ES: "En el sitio",
            IT: "Sul posto",
            NL: "Ter plaatse",
            PT: "No local",
        },
    },
    {
        id: "sale_mode_2",
        name: "Takeaway",
        active: true,
        isFavorite: false,
        displayName: {
            FR: "À emporter",
            EN: "Takeaway",
            ES: "Para llevar",
            IT: "Da asporto",
            NL: "Afhalen",
            PT: "Para levar",
        },
    },
    {
        id: "sale_mode_3",
        name: "Delivery",
        active: false,
        isFavorite: false,
        displayName: {
            FR: "Livraison",
            EN: "Delivery",
            ES: "Entrega",
            IT: "Consegna",
            NL: "Levering",
            PT: "Entrega",
        },
    },
];

export const DEFAULT_PAYMENT_MODES: POSPaymentModeItem[] = [
    {
        id: "payment_mode_1",
        name: "CreditCard",
        active: true,
        isFavorite: true,
        displayName: {
            FR: "Carte bancaire",
            EN: "Credit card",
            ES: "Tarjeta de crédito",
            IT: "Carta di credito",
            NL: "Creditcard",
            PT: "Cartão de crédito",
        },
    },
    {
        id: "payment_mode_2",
        name: "Cash",
        active: true,
        isFavorite: false,
        displayName: {
            FR: "Espèces",
            EN: "Cash",
            ES: "Efectivo",
            IT: "Contanti",
            NL: "Contant",
            PT: "Dinheiro",
        },
    },
    {
        id: "payment_mode_3",
        name: "MealVoucher",
        active: false,
        isFavorite: false,
        displayName: {
            FR: "Ticket restaurant",
            EN: "Meal voucher",
            ES: "Vale de comida",
            IT: "Buono pasto",
            NL: "Maaltijdcheque",
            PT: "Vale-refeição",
        },
    },
];

export const DEFAULT_LANGUAGES: POSLanguageItem[] = [
    {
        id: "lang_fr",
        name: "French",
        flag: "fr",
        title: "Français",
        active: true,
        isFavorite: true,
        isDefault: true,
    },
    {
        id: "lang_en",
        name: "English",
        flag: "gb",
        title: "English",
        active: false,
        isFavorite: false,
        isDefault: false,
    },
    {
        id: "lang_es",
        name: "Spanish",
        flag: "es",
        title: "Español",
        active: false,
        isFavorite: false,
        isDefault: false,
    },
    {
        id: "lang_it",
        name: "Italian",
        flag: "it",
        title: "Italiano",
        active: false,
        isFavorite: false,
        isDefault: false,
    },
    {
        id: "lang_nl",
        name: "Dutch",
        flag: "nl",
        title: "Nederlands",
        active: false,
        isFavorite: false,
        isDefault: false,
    },
    {
        id: "lang_pt",
        name: "Portuguese",
        flag: "pt",
        title: "Português",
        active: false,
        isFavorite: false,
        isDefault: false,
    },
];

export const DEFAULT_INFORMATION_MODES: POSInformationModeItem[] = [
    {
        id: "info_mode_1",
        name: "Table",
        active: {},
        isFavorite: false,
        displayName: {
            FR: "Numéro de table",
            EN: "Table number",
            ES: "Número de mesa",
            IT: "Numero del tavolo",
            NL: "Tafelnummer",
            PT: "Número da mesa",
        },
    },
    {
        id: "info_mode_2",
        name: "Name",
        active: {},
        isFavorite: false,
        displayName: {
            FR: "Nom du client",
            EN: "Customer name",
            ES: "Nombre del cliente",
            IT: "Nome del cliente",
            NL: "Klantnaam",
            PT: "Nome do cliente",
        },
    },
    {
        id: "info_mode_3",
        name: "Buzzer",
        active: {},
        isFavorite: false,
        displayName: {
            FR: "Bipeur",
            EN: "Buzzer",
            ES: "Avisador",
            IT: "Cicalino",
            NL: "Buzzer",
            PT: "Campainha",
        },
    },
];

export const DEFAULT_POS_CONFIG: POSConfig = {
    SalesMode: {
        items: DEFAULT_SALES_MODES,
    },
    PaymentMode: {
        skipStep: false,
        items: DEFAULT_PAYMENT_MODES,
    },
    Language: {
        items: DEFAULT_LANGUAGES,
    },
    InformationMode: {
        skipStep: false,
        items: DEFAULT_INFORMATION_MODES,
    },
    PaperlessTicket: {
        isActive: false,
        isEmailActive: false,
        isConfirmationEmailActive: false,
        isUnauthenticatedEmailActive: false,
    },
};

/** Règles de validation d'un template POS */
export interface PosConfigValidation {
    isValid: boolean;
    errors: string[];
}

/**
 * Required POS categories that must contain at least one active item.
 * - `key`: i18n key suitable for `t(key)` in UI (e.g. "Sale mode").
 * - `validationMessage`: English message stored in the slice on save.
 * - `getItems`: extracts the array of items from a POSConfig-like object.
 */
export const POS_REQUIRED_CATEGORIES: Array<{
    key: string;
    validationMessage: string;
    getItems: (
        tmpl: Partial<POSConfig>
    ) => Array<{ active: boolean }> | undefined;
}> = [
    {
        key: "Sale mode",
        validationMessage: "At least one sale mode must be active",
        getItems: (tmpl) => tmpl.SalesMode?.items,
    },
    {
        key: "Payment mode",
        validationMessage: "At least one payment mode must be active",
        getItems: (tmpl) => tmpl.PaymentMode?.items,
    },
    {
        key: "Language",
        validationMessage: "At least one language must be active",
        getItems: (tmpl) => tmpl.Language?.items,
    },
];

/**
 * Return the i18n keys for categories that have no active item in the
 * given template. Callers translate the keys via `t(key)` when rendering.
 */
export function getTemplateMissingKeys(
    template: POSConfig | Partial<POSConfig> | Record<string, unknown>
): string[] {
    const tmpl = template as Partial<POSConfig>;
    return POS_REQUIRED_CATEGORIES.filter((cat) => {
        const items = cat.getItems(tmpl) || [];
        return !items.some((i) => i.active === true);
    }).map((cat) => cat.key);
}

export function validatePosConfig(
    config: POSConfig,
    t?: (key: string) => string
): PosConfigValidation {
    const errors: string[] = [];
    for (const cat of POS_REQUIRED_CATEGORIES) {
        const items = cat.getItems(config) || [];
        if (!items.some((i) => i.active === true)) {
            errors.push(t ? t(cat.validationMessage) : cat.validationMessage);
        }
    }

    // InformationMode: peut exister sans aucun mode activé (pas de validation)

    return {
        isValid: errors.length === 0,
        errors,
    };
}
